import {EnzymeCutsiteData} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {ValidationStatus} from 'molbio/bulk-assembly/BulkAssemblyValidationConstants';
import {deleteFragmentsActionFunction} from 'molbio/bulk-assembly/tables/action-functions/deleteActionFunctions';
import {statusFilterFragmentsActionFunction} from 'molbio/bulk-assembly/tables/action-functions/statusFilterActionFunction';
import {
  viewFragmentsActionFunction,
  viewFragmentsAxisContextMenuActionFunction,
} from 'molbio/bulk-assembly/tables/action-functions/viewActionFunctions';
import {downloadCSVActionFunction} from 'notebook/structured-tables/action-functions/downloadCSVActionFunction';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  ActionFunctions,
  StructuredTable,
  StructuredTableColumn,
  StructuredTableSchema,
} from 'notebook/structured-tables/StructuredTableConstants';
import {RowMetadata, Table, TableDimension, TableReorderLocks} from 'notebook/tables/TableConstants';

export interface BulkAssemblyFragmentsStructuredTableMetadata {
  columns: Array<StructuredTableColumn>;
  onViewFragments: (fragmentCids: Array<string>) => void;
  isFilterDisabledByStatus: {[key in ValidationStatus]: boolean};
  isFiltered: boolean;
  isReadOnly: boolean;
  onDeleteAll: () => void;
  onDeleteFiltered: () => void;
  onDeleteRange: (startIndex: number, endIndex: number) => void;
  statusFilter: ValidationStatus | null;
  onSetStatusFilter: (newStatusFilter: ValidationStatus | null) => void;
  tableRowsName: string;
}

export interface BulkAssemblyFragmentsStructuredTableRowMetadata extends RowMetadata {
  fragmentCid: string | null;
  polymerVersionId: number | null;
  enzymeCutsiteData5: EnzymeCutsiteData | null;
  enzymeCutsiteData3: EnzymeCutsiteData | null;
}

export type BulkAssemblyFragmentsStructuredTable = StructuredTable<
  BulkAssemblyFragmentsStructuredTableMetadata,
  BulkAssemblyFragmentsStructuredTableRowMetadata
>;

/**
 * Structured table adapter describing the behavior of the fragments table in bulk assembly.
 *
 * This table is never used in the notebook, and has cell values controlled by the surrounding assembly code
 * as part of the overall assembly UI.
 */
export default class BulkAssemblyFragmentsStructuredTableAdapter extends GenericStructuredTableAdapter<
  BulkAssemblyFragmentsStructuredTableMetadata,
  BulkAssemblyFragmentsStructuredTableRowMetadata
> {
  static getAdapter(): BulkAssemblyFragmentsStructuredTableAdapter {
    return getStructuredTableAdapters()
      .BULK_ASSEMBLY_FRAGMENTS as BulkAssemblyFragmentsStructuredTableAdapter;
  }

  getBlankRowMetadata(
    tableMetadata: BulkAssemblyFragmentsStructuredTableMetadata
  ): BulkAssemblyFragmentsStructuredTableRowMetadata {
    // Note that even if the fragment production method involves enzymes, blank rows will have their
    // endpoint cells locked initially so this data is fine to be null.
    return {
      fragmentCid: null,
      polymerVersionId: null,
      enzymeCutsiteData5: null,
      enzymeCutsiteData3: null,
    };
  }

  getDisplayName(structuredTable: BulkAssemblyFragmentsStructuredTable): string {
    return 'Fragments';
  }

  _getAllSupportedColumns({
    metadata,
  }: {
    metadata: BulkAssemblyFragmentsStructuredTableMetadata;
  }): Array<StructuredTableColumn> {
    // Columns are dynamically decided at render time, so specify them as table metadata rather
    // than defining statically.
    return metadata.columns;
  }

  getActionFunctions(
    tableMetadata: BulkAssemblyFragmentsStructuredTableMetadata,
    schemas: Array<StructuredTableSchema>
  ): ActionFunctions<
    BulkAssemblyFragmentsStructuredTableMetadata,
    BulkAssemblyFragmentsStructuredTableRowMetadata
  > {
    return {
      overflow: [downloadCSVActionFunction],
      secondary: [
        statusFilterFragmentsActionFunction,
        viewFragmentsActionFunction,
        deleteFragmentsActionFunction,
      ],
      contextMenu: [viewFragmentsAxisContextMenuActionFunction],
    };
  }

  toTable(fragmentsTable: BulkAssemblyFragmentsStructuredTable): Table {
    const table = super.toTable(fragmentsTable);
    return {...table, allowColumnEdits: false, lockedDims: true, allowNoRows: true};
  }

  allowReorderDimensions(): TableReorderLocks {
    return {
      [TableDimension.ROW]: false,
      [TableDimension.COLUMN]: false,
    };
  }
}
